import { ITheme } from "@/schemas/theme/themeSchema";
import { loadStyle } from "@/utils/loadStyle";
const defaultDarkColores = {
  900: "#13171e",
  800: "#1f2329",
  700: "#272a30",
  600: "#32363e",
  500: "#3c4048",
  400: "#494d55",
  300: "#575b62",
  200: "#828488",
  100: "#cbcbcd",
  50: "#f7f7f8",
  10: "#fbfbfc",
  "accent-1": "#409eff",
  "accent-2": "#00de52",
};

const defaultLightColores = {
  900: "#f7f7f8",
  800: "#f1f1f2",
  700: "#ebebec",
  600: "#dfdfe0",
  500: "#d7d8d9",
  400: "#c9cacb",
  300: "#a4a5a7",
  200: "#8d8f92",
  100: "#727477",
  50: "#4b4c4e",
  10: "#2b2b2c",
  "accent-1": "#35aaff",
  "accent-2": "#0fce56",
};

export default function setTheme(theme: ITheme): void {
  if (theme.preset === "dark" || theme.preset === "light") {
    setColores(theme);
  } else {
    theme.preset = "dark";
    setColores(theme);
  }

  if (theme.borderRadius) {
    if (theme.borderRadius.small) {
      document.documentElement.style.setProperty(
        "--b-radius-small",
        theme.borderRadius.small
      );
      document.documentElement.style.setProperty(
        "--r-md",
        theme.borderRadius.small
      );
    }

    if (theme.borderRadius.large) {
      document.documentElement.style.setProperty(
        "--b-radius-large",
        theme.borderRadius.large
      );
      document.documentElement.style.setProperty(
        "--r-lg",
        theme.borderRadius.large
      );
    }

    if (theme.borderRadius.extraSmall) {
      document.documentElement.style.setProperty(
        "--r-xs",
        theme.borderRadius.extraSmall
      );
    }

    if (theme.borderRadius.extraLarge) {
      document.documentElement.style.setProperty(
        "--r-xl",
        theme.borderRadius.extraLarge
      );
    }
  }

  // Set canvas color
  document.documentElement.style.setProperty(
    "--c-email-canvas",
    theme.canvasColor || "#eaeaea"
  );

  setFont(theme);
}

function setFont(theme: ITheme): void {
  if (!theme.font) {
    return;
  }

  if (theme.font.url) {
    loadStyle(theme.font.url);
  }

  if (theme.font.family) {
    const family = withSansSerifFallback(theme.font.family);
    document.documentElement.style.setProperty("--font-family-primary", family);
  }
}

function withSansSerifFallback(family: string): string {
  return family.includes(",") ? family : `${family}, sans-serif`;
}

function setColores(theme: ITheme) {
  if (theme.colors && theme.colors[900] !== undefined) {
    document.documentElement.style.setProperty("--c-g900", theme.colors[900]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g900",
        defaultLightColores[900]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g900",
        defaultDarkColores[900]
      );
    }
  }
  if (theme.colors && theme.colors[800] !== undefined) {
    document.documentElement.style.setProperty("--c-g800", theme.colors[800]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g800",
        defaultLightColores[800]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g800",
        defaultDarkColores[800]
      );
    }
  }
  if (theme.colors && theme.colors[700] !== undefined) {
    document.documentElement.style.setProperty("--c-g700", theme.colors[700]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g700",
        defaultLightColores[700]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g700",
        defaultDarkColores[700]
      );
    }
  }
  if (theme.colors && theme.colors[600] !== undefined) {
    document.documentElement.style.setProperty("--c-g600", theme.colors[600]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g600",
        defaultLightColores[600]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g600",
        defaultDarkColores[600]
      );
    }
  }
  if (theme.colors && theme.colors[500] !== undefined) {
    document.documentElement.style.setProperty("--c-g500", theme.colors[500]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g500",
        defaultLightColores[500]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g500",
        defaultDarkColores[500]
      );
    }
  }
  if (theme.colors && theme.colors[400] !== undefined) {
    document.documentElement.style.setProperty("--c-g400", theme.colors[400]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g400",
        defaultLightColores[400]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g400",
        defaultDarkColores[400]
      );
    }
  }
  if (theme.colors && theme.colors[300] !== undefined) {
    document.documentElement.style.setProperty("--c-g300", theme.colors[300]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g300",
        defaultLightColores[300]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g300",
        defaultDarkColores[300]
      );
    }
  }
  if (theme.colors && theme.colors[200] !== undefined) {
    document.documentElement.style.setProperty("--c-g200", theme.colors[200]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g200",
        defaultLightColores[200]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g200",
        defaultDarkColores[200]
      );
    }
  }
  if (theme.colors && theme.colors[100] !== undefined) {
    document.documentElement.style.setProperty("--c-g100", theme.colors[100]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g100",
        defaultLightColores[100]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g100",
        defaultDarkColores[100]
      );
    }
  }
  if (theme.colors && theme.colors[50] !== undefined) {
    document.documentElement.style.setProperty("--c-g50", theme.colors[50]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g50",
        defaultLightColores[50]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g50",
        defaultDarkColores[50]
      );
    }
  }
  if (theme.colors && theme.colors[10] !== undefined) {
    document.documentElement.style.setProperty("--c-g10", theme.colors[10]);
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-g10",
        defaultLightColores[10]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-g10",
        defaultDarkColores[10]
      );
    }
  }
  if (theme.colors && theme.colors.white) {
    document.documentElement.style.setProperty("--c-white", theme.colors.white);
  }
  if (theme.colors && theme.colors.primary) {
    document.documentElement.style.setProperty(
      "--c-structure-1",
      theme.colors.primary
    );
  }
  if (theme.colors && theme.colors["primary-light"]) {
    document.documentElement.style.setProperty(
      "--c-structure-2",
      theme.colors["primary-light"]
    );
  }
  if (theme.colors && theme.colors["primary-light-2"]) {
    document.documentElement.style.setProperty(
      "--c-structure-3",
      theme.colors["primary-light-2"]
    );
  }
  if (theme.colors && theme.colors.secondary) {
    document.documentElement.style.setProperty(
      "--c-content-1",
      theme.colors.secondary
    );
  }
  if (theme.colors && theme.colors["secondary-light"]) {
    document.documentElement.style.setProperty(
      "--c-content-2",
      theme.colors["secondary-light"]
    );
  }
  if (theme.colors && theme.colors["secondary-light-2"]) {
    document.documentElement.style.setProperty(
      "--c-content-3",
      theme.colors["secondary-light-2"]
    );
  }
  if (theme.colors && theme.colors.error) {
    document.documentElement.style.setProperty(
      "--c-destructive-1",
      theme.colors.error
    );
  }
  if (theme.colors && theme.colors["error-light"]) {
    document.documentElement.style.setProperty(
      "--c-destructive-2",
      theme.colors["error-light"]
    );
  }
  if (theme.colors && theme.colors.success) {
    document.documentElement.style.setProperty(
      "--c-confirm-1",
      theme.colors.success
    );
  }
  if (theme.colors && theme.colors["success-light"]) {
    document.documentElement.style.setProperty(
      "--c-confirm-2",
      theme.colors["success-light"]
    );
  }
  if (theme.colors && theme.colors.active) {
    document.documentElement.style.setProperty(
      "--c-accent-1",
      theme.colors.active
    );
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-accent-1",
        defaultLightColores["accent-1"]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-accent-1",
        defaultDarkColores["accent-1"]
      );
    }
  }
  if (theme.colors && theme.colors["active-2"]) {
    document.documentElement.style.setProperty(
      "--c-accent-2",
      theme.colors["active-2"]
    );
  } else {
    if (theme.preset === "light") {
      document.documentElement.style.setProperty(
        "--c-accent-2",
        defaultLightColores["accent-2"]
      );
    } else {
      document.documentElement.style.setProperty(
        "--c-accent-2",
        defaultDarkColores["accent-2"]
      );
    }
  }
  if (theme.colors && theme.colors["active-3"]) {
    document.documentElement.style.setProperty(
      "--c-accent-3",
      theme.colors["active-3"]
    );
  }
  if (theme.colors && theme.colors["locked-structure"]) {
    document.documentElement.style.setProperty(
      "--c-locked-structure",
      theme.colors["locked-structure"]
    );
  }
  if (theme.colors && theme.colors["locked-content"]) {
    document.documentElement.style.setProperty(
      "--c-locked-content",
      theme.colors["locked-content"]
    );
  }
}
